/**
 * Main menu functionality for responsive behaviors and dropdowns.
 *
 * @package  Embark
 * @since    1.0.0
 */

/**
 * Import dependencies.
 */
import $ from 'jquery';
import hoverintent from 'hoverintent';
import { disableScroll, enableScroll, isElementInViewport } from '../global/helpers';
import { els } from './variables';

/**
 * Set menuBreakpoint using the value set in head.js for the current site.
 * Each site has a unique logo with variable width, and therefore, the point
 * at which the site needs to change from the desktop to mobile menu varies.
 * Using a JS approach avoids a LOT of duplicate styles in the stylesheet,
 * as the same code would need to be output up to 11 times (once per site).
 * A class is added to the html element within head.js right away to avoid
 * any layout flickers when the page loads. This file then updates that class
 * on resize.
 */
const { mainMenuBreakpoint } = window.emOptions;
// Check if the current browser has mouse support.
const hasMouse = matchMedia('(pointer:fine)').matches || (!!window.MSInputMethodContext && !!document.documentMode);

/**
 * Check if the site should be displaying the mobile menu or not.
 *
 * The mobile menu is displayed if the screen width is less than mainMenuBreakpoint
 * or the user is on a touch device that doesn't have mouse support.
 */
export function isMobileMenu() {
	return window.innerWidth < mainMenuBreakpoint || ('ontouchstart' in window && !hasMouse) || (navigator.maxTouchPoints > 0 && !hasMouse) || (navigator.msMaxTouchPoints > 0 && !hasMouse);
}

/**
 * Check if the site should be displaying the desktop menu or not.
 */
export function isDesktopMenu() {
	return !isMobileMenu();
}

/**
 * Menu items that have submenus.
 *
 * @type {NodeList}
 */
const menuItemsWithChildren = document.querySelectorAll('.dropdown-menu .menu-item-has-children');

/**
 * Class used for menu item triggers.
 *
 * @type {String}
 */
const menuItemTriggerClass = 'menu-item__trigger';

/**
 * Class added to <html> when menu is open.
 *
 * @type {String}
 */
const menuOpenHtmlClass = 'html--menu-open';

/**
 * Class added to navicon when menu is open.
 *
 * @type {String}
 */
const menuOpenNaviconClass = 'navicon__trigger--open';

const siteNotice = els.header ? els.header.querySelector('.site-notice') : null;

/**
 * Toggle mobile submenus when a menu item link with a hash or the
 * menu item trigger is clicked.
 *
 * @param  {Event} e
 */
function handleMenuItemTriggerClick(e) {
	if (isMobileMenu()) {
		/**
		 * Prevents further propagation of the current event in the capturing and bubbling phases.
		 */
		e.stopPropagation();

		// Get the submenu based on what was clicked.
		let submenu;
		if (e.target.href) {
			const hash = e.target.href.substring(e.target.href.lastIndexOf('/') + 1);
			if (hash && hash === '#') {
				submenu = e.target.nextElementSibling.nextElementSibling;
			}
		} else if (e.target.classList.contains(menuItemTriggerClass)) {
			submenu = e.target.nextElementSibling;
		}

		// If we have a submenu, expand it.
		if (submenu) {
			e.preventDefault();
			const $submenu = $(submenu);
			const $menuItem = $submenu.closest('.menu-item');
			$menuItem.find('> a').attr('aria-expanded', (index, attr) => (attr === 'true' ? 'false' : 'true'));
			$menuItem.toggleClass('open');
			$submenu.slideToggle();
		}
	}
}

/**
 * Maybe close opened submenus.
 */
function maybeCloseSubmenus() {
	if (els.header) {
		const openMenuItems = els.header.querySelectorAll('.menu-item.open');
		if (openMenuItems && isDesktopMenu()) {
			openMenuItems.forEach((menuItem) => {
				menuItem.classList.remove('open');
				const submenus = menuItem.querySelectorAll('.sub-menu');
				submenus.forEach((submenu) => {
					submenu.style.display = '';
				});
			});
		}
	}
}

/**
 * Check if current submenu is fully within the viewport.
 * If not, add a class so that it can be styled accordingly.
 *
 * @param  {Event} e
 */
function checkSubmenuPosition() {
	const submenu = this.querySelector('.sub-menu');
	if (submenu && !isElementInViewport(submenu) && isDesktopMenu()) {
		this.classList.add('submenu-offscreen');
	}
}

/**
 * Add class to the first .current-menu-ancestor to avoid multiple being highlighted.
 *
 * @param  {Event} e
 */
function handleMultipleMenuAncestors() {
	const ancestorItems = document.querySelectorAll('.main-menu > .current-menu-ancestor');
	if (ancestorItems.length > 0) {
		ancestorItems[0].classList.add('current-menu-ancestor--primary');
	}
}

/**
 * Open the mobile menu.
 */
function openMobileMenu() {
	if (!els.naviconTrigger) {
		return;
	}

	disableScroll();

	if (siteNotice) {
		let translateY = siteNotice.clientHeight + 1;
		const noticeStyles = window.getComputedStyle(siteNotice);
		translateY += parseInt(noticeStyles.getPropertyValue('margin-bottom'));
		els.header.setAttribute('data-prev-style', els.header.style.cssText);
		els.header.style.transitionProperty = 'transform';
		els.header.style.transform = `translateY(${-translateY}px)`;
	}

	els.html.classList.add(menuOpenHtmlClass);
	els.naviconTrigger.classList.add(menuOpenNaviconClass);
}

/**
 * Close the mobile menu.
 */
function closeMobileMenu() {
	if (!els.naviconTrigger) {
		return;
	}

	if (!document.documentElement.classList.contains('lightbox-open')) {
		enableScroll();
	}

	if (siteNotice) {
		const { prevStyle } = els.header.dataset;
		if (prevStyle) {
			els.header.setAttribute('style', prevStyle);
			els.header.removeAttribute('data-prev-style');
		}
	}

	els.html.classList.remove(menuOpenHtmlClass);
	els.naviconTrigger.classList.remove(menuOpenNaviconClass);
}

/**
 * Toggle the mobile menu.
 */
function toggleMobileMenu() {
	if (els.html.classList.contains(menuOpenHtmlClass)) {
		closeMobileMenu();
	} else {
		openMobileMenu();
	}
}

/**
 * Close the mobile menu when you click off of it.
 *
 * @param  {Event} e
 */
function handleMenuState(e) {
	if (!els.navicon) {
		return;
	}

	/**
	 * If the menu is open, and the event target is the nav or a child of the nav, exit quickly.
	 */
	if (
		els.html.classList.contains(menuOpenHtmlClass)
		&& (e.target === els.navContainer || $(e.target).closest(els.navContainer).length > 0)
	) {
		return;
	}

	/**
	 * If event target is the navicon or any of its children, trigger the toggle method.
	 */
	if (e.target === els.navicon || $(e.target).closest('.navicon__trigger').length > 0) {
		toggleMobileMenu();
	} else {
		/**
		 * Else, this is not the navicon trigger. Close the menu. User has clicked away.
		 */
		closeMobileMenu();
	}
}

/**
 * Update mobile menu top offset and close opened submenus depending on screensize.
 *
 * @param  {Event} e
 */
function handleMenuOnResize() {
	window.requestAnimationFrame(() => {
		maybeCloseSubmenus();

		if (isDesktopMenu()) {
			els.html.classList.add('menu-is-desktop');
			els.html.classList.remove('menu-is-mobile');
			closeMobileMenu();
		} else {
			els.html.classList.add('menu-is-mobile');
			els.html.classList.remove('menu-is-desktop');
		}
	});
}

/**
 * Initialze menu.
 *
 * @param  {Event} e [description]
 */
function initMainMenu() {
	handleMultipleMenuAncestors();

	/**
	 * Add event listeners for menu items with submenus.
	 */
	if (menuItemsWithChildren) {
		menuItemsWithChildren.forEach((item) => {
			hoverintent(item, () => {
				item.classList.add('hover');
			}, () => {
				item.classList.remove('hover');
			}).options({
				timeout: 200,
			});
			item.addEventListener('mouseenter', checkSubmenuPosition);
			item.addEventListener('click', handleMenuItemTriggerClick);
		});
	}

	// Add "open" class on page load so mobile-menu is expanded to currently viewed sub-menu items.
	if (isMobileMenu()) {
		const $currentMenuItems = $('.main-menu [class*="current"].menu-item-has-children');
		if ($currentMenuItems.length > 0) {
			$currentMenuItems.addClass('open').find('> .sub-menu').show();
		}
	}
}

window.addEventListener('DOMContentLoaded', initMainMenu);
window.addEventListener('resize', handleMenuOnResize);
document.addEventListener('click', handleMenuState);
